import { useState } from "react";
import { Palette, Download, Loader2 } from "lucide-react";
import type { PaletteRecord } from "../../lib/database";
import { tokenEngineService } from "../../lib/tokenEngineIntegration";
import { db } from "../../lib/database";

interface TokenPreviewProps {
  palette: PaletteRecord;
}

export function TokenPreview({ palette }: TokenPreviewProps) {
  const [isGenerating, setIsGenerating] = useState(false);

  const generateTokens = async () => {
    if (!palette.id) return;

    setIsGenerating(true);
    try {
      const result = await tokenEngineService.generateTokensFromPalette(
        palette.id,
        {
          namingConvention: "systematic",
          includeSemanticTokens: true,
          includeAliases: true,
        },
      );

      if (!result.validation.valid) {
        console.error("Token validation failed:", result.validation.errors);
        return;
      }

      const exportResult = await tokenEngineService.exportTokens(
        result.tokenSet,
        {
          platform: "css",
          includeMetadata: true,
          includeComments: true,
          minify: false,
          namingConvention: "kebab-case",
        },
      );

      const blob = new Blob([exportResult.content], { type: "text/css" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportResult.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      if (palette.id) {
        await db.logActivity({
          type: "palette_exported",
          paletteId: palette.id,
          paletteName: palette.name,
          description: `Generated design tokens from palette "${palette.name}"`,
          details: {
            exportFormat: "css",
            tokenCount: exportResult.tokens,
          },
        });
      }
    } catch (error) {
      console.error("Failed to generate tokens:", error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="mt-6 pal-section p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
        <Palette className="w-4 h-4" />
        Token Preview
      </h3>
      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
              CSS Variables
            </p>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded p-3 font-mono text-xs">
              {palette.colors
                .slice(0, 3)
                .map((color, index) => (
                  <div key={index} className="mb-1">
                    <span className="pal-token-variable">
                      --color-{index + 1}:
                    </span>{" "}
                    <span className="text-blue-600 dark:text-blue-400">
                      {color};
                    </span>
                  </div>
                ))}
              {palette.colors.length > 3 && (
                <div className="text-gray-400 dark:text-gray-500 italic">
                  ...and {palette.colors.length - 3} more colors
                </div>
              )}
            </div>
          </div>
          <div>
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
              Semantic Suggestions
            </p>
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded p-3 text-xs">
              <div className="space-y-1">
                <div>
                  <span className="pal-token-variable">--primary:</span>{" "}
                  <span className="text-green-600 dark:text-green-400">
                    {palette.colors[0] || "#000000"}
                  </span>
                </div>
                <div>
                  <span className="pal-token-variable">--background:</span>{" "}
                  <span className="text-blue-600 dark:text-blue-400">
                    {palette.colors.find((c) => {
                      const l =
                        (0.2126 * parseInt(c.slice(1, 3), 16)) / 255 +
                        (0.7152 * parseInt(c.slice(3, 5), 16)) / 255 +
                        (0.0722 * parseInt(c.slice(5, 7), 16)) / 255;
                      return l > 0.8;
                    }) ||
                      palette.colors[0] ||
                      "#ffffff"}
                  </span>
                </div>
                <div>
                  <span className="pal-token-variable">--text-primary:</span>{" "}
                  <span className="text-purple-600 dark:text-purple-400">
                    {palette.colors.find((c) => {
                      const l =
                        (0.2126 * parseInt(c.slice(1, 3), 16)) / 255 +
                        (0.7152 * parseInt(c.slice(3, 5), 16)) / 255 +
                        (0.0722 * parseInt(c.slice(5, 7), 16)) / 255;
                      return l < 0.3;
                    }) ||
                      palette.colors[1] ||
                      "#000000"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-600">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Preview of design tokens that would be generated from this palette
          </p>
          <button
            onClick={generateTokens}
            disabled={isGenerating}
            className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 text-white text-xs rounded-md transition-colors flex items-center gap-1.5"
          >
            {isGenerating ? (
              <Loader2 className="w-3 h-3 animate-spin" />
            ) : (
              <Download className="w-3 h-3" />
            )}
            Generate Full Token Set
          </button>
        </div>
      </div>
    </div>
  );
}
